import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { mockEvaluation } from '@/mocks/evaluaciones.mock';
import DatosDeEjemploBanner from '@/shared/components/DatosDeEjemploBanner';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';


function getRatingStyle(rating: number, max: number): { bg: string; text: string; label: string } {
  const pct = rating / max;
  if (pct >= 0.7) return { bg: 'bg-green-100', text: 'text-green-700', label: 'Aprobado' };
  if (pct >= 0.5) return { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Casi reprobado' };
  return { bg: 'bg-red-100', text: 'text-red-700', label: 'Reprobado' };
}

export const EvaluationDetail: React.FC = () => {
  const navigate = useNavigate();
  // El id de la ruta no se usa: la pantalla es maqueta y siempre muestra la
  // misma evaluación de ejemplo. Cuando llegue su backend, este es el valor con
  // el que habrá que pedirla.
  useParams<{ evaluationId: string }>();
  const ratingStyle = getRatingStyle(mockEvaluation.finalRating, mockEvaluation.maxRating);

  return (
    <div>
      <DatosDeEjemploBanner detalle="El módulo de evaluaciones todavía no está conectado al servidor: esta evaluación es de muestra." />
      {/* Header */}
      <div className="flex items-center justify-between my-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/rh/evaluations')}
            className={estiloDeBoton('secundario', 'mb-6')}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
          <div>
            <TituloDePantalla nivel="interna">
              Ver evaluación de proveedor
            </TituloDePantalla>
            <p className="text-[#1E1E1F] text-sm">
              Visualiza la información de la evaluación
            </p>
          </div>
        </div>
      </div>

      {/* Contenido */}
      <div className="bg-white border border-[#E0E0E0] rounded-xl p-6 space-y-6">

        {/* Información General */}
        <div className="border border-[#E0E0E0] rounded-xl">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-[#1E1E1F]">
              Información general
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#6B7280] mb-1">
                  Proveedor
                </label>
                <p className="text-sm text-[#1E1E1F] font-medium">
                  {mockEvaluation.supplier}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#6B7280] mb-1">
                  RFC o razón social
                </label>
                <p className="text-sm text-[#1E1E1F] font-medium">
                  {mockEvaluation.rfc}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#6B7280] mb-1">
                  Evaluador
                </label>
                <p className="text-sm text-[#1E1E1F] font-medium">
                  {mockEvaluation.evaluator}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#6B7280] mb-1">
                  Fecha de evaluación
                </label>
                <p className="text-sm text-[#1E1E1F] font-medium">
                  {mockEvaluation.evaluationDate}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Periodo Evaluado */}
        <div className="border border-[#E0E0E0] rounded-xl">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-[#1E1E1F]">
              Periodo evaluado
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#6B7280] mb-1">
                  Mes/año
                </label>
                <p className="text-sm text-[#1E1E1F] font-medium">
                  {mockEvaluation.period}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#6B7280] mb-1">
                  Proyecto
                </label>
                <p className="text-sm text-[#1E1E1F] font-medium">
                  {mockEvaluation.project}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Criterios de Evaluación */}
        <div className="border border-[#E0E0E0] rounded-xl">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-[#1E1E1F]">
              Criterios de evaluación
            </h2>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {mockEvaluation.criteria.map((criterion) => (
                <div key={criterion.id} className="flex items-center justify-between py-2 border-b border-[#F0F0F0] last:border-0">
                  <span className="text-sm text-[#1E1E1F]">
                    {criterion.id}. {criterion.name}
                  </span>
                  <span className="text-sm font-semibold text-[#1E1E1F]">
                    {criterion.score}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-[#E0E0E0]">
              <label className="block text-sm font-medium text-[#6B7280] mb-2">
                Comentarios generales
              </label>
              <p className="text-sm text-[#1E1E1F] leading-relaxed">
                {mockEvaluation.generalComments}
              </p>
            </div>
          </div>
        </div>

        {/* Calificación Final */}
        <div className="border border-[#E0E0E0] rounded-xl">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-[#1E1E1F]">
              Calificación final
            </h2>
          </div>
          <div className="p-6">
            <div className={`${ratingStyle.bg} rounded-xl p-6 inline-flex items-center gap-4`}>
              <p className={`text-3xl font-bold ${ratingStyle.text}`}>
                {mockEvaluation.finalRating}
                <span className="text-lg font-medium ml-1">
                  / {mockEvaluation.maxRating}
                </span>
              </p>
              <span className={`text-sm font-semibold ${ratingStyle.text}`}>
                {ratingStyle.label}
              </span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};
